import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getIndexerErrorInfo } from '../api/errors';
import {
  useLazyGetProjectOverviewQuery,
  useLazyListProjectJobsQuery,
} from '../api/indexerApi';
import type { ProjectJob, ProjectOverviewData } from '../api/types';
import { CoverageBreakdownTable } from '../components/charts/CoverageBreakdownTable';
import { DonutChart } from '../components/charts/DonutChart';
import { OverviewAdvancedCharts } from '../components/charts/OverviewAdvancedCharts';
import { StackedBarChart } from '../components/charts/StackedBarChart';
import { IndexerChartCard } from '../components/IndexerChartCard';
import { IndexerKpiCard } from '../components/IndexerKpiCard';
import { NeedProject } from '../components/NeedProject';
import { OverviewSettingsChecklist } from '../components/OverviewSettingsChecklist';
import { IndexerIcon } from '../components/IndexerIcon';
import { OverviewPagePreload } from '../components/preloads/IndexerPreloads';
import {
  OverviewTabSwitch,
  type OverviewTab,
} from '../components/OverviewTabSwitch';
import { InspectQuotaHelpTip } from '../components/InspectQuotaHelpTip';
import { IndexLimitHelpTip } from '../components/IndexLimitHelpTip';
import {
  GOOGLE_DAILY_QUOTA,
  INSPECT_DAILY_QUOTA,
} from '../components/StatsCards';
import {
  changesPath,
  changesSliceToFilter,
  inventorySliceToStatus,
  urlsPath,
} from '../lib/drilldown-links';
import { useProject } from '../lib/project-context';
import { useIndexerPaths } from '../lib/indexer-paths';
import {
  indexOutcomeBarSeries,
  inspectDailyBarSeries,
} from '../lib/chart-colors';
import {
  aggregateIndexJobsByDay,
  aggregateInspectJobsByDay,
  changesDonutSlices,
  coverageTableRows,
  inventoryDonutSlices,
  pctOf,
} from '../lib/overview-stats';
import {
  googleIndexingUsedInRange,
  inspectUsedInRange,
  jobsInRange,
  startOfRange,
  STATS_RANGE_OPTIONS,
  statsRangeHint,
  type StatsRange,
} from '../lib/job-range';
import { toUtcIso } from '../lib/datetime';
import { formatFaNumber } from '../lib/utils';

type KpiCard = {
  id: string;
  label?: string;
  value: string;
  hint?: string;
  tone: 'primary' | 'success' | 'warning' | 'danger' | 'neutral';
  /** راهنما کنار عدد (نه کنار برچسب) */
  help?: ReactNode;
  href?: string;
};

export function OverviewPage() {
  const paths = useIndexerPaths();
  const { project, projectRef } = useProject();
  const navigate = useNavigate();
  const [rangeDays, setRangeDays] = useState<StatsRange>(7);
  const [overview, setOverview] = useState<ProjectOverviewData | null>(null);
  const [jobs, setJobs] = useState<ProjectJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageTab, setPageTab] = useState<OverviewTab>('summary');
  const [getProjectOverview] = useLazyGetProjectOverviewQuery();
  const [listProjectJobs] = useLazyListProjectJobsQuery();

  const load = useCallback(async () => {
    if (!project || !projectRef) return;
    setLoading(true);
    setError(null);
    try {
      const since = toUtcIso(startOfRange(rangeDays))!;
      const [overviewData, listedJobs] = await Promise.all([
        getProjectOverview({ projectId: projectRef, days: rangeDays }, true).unwrap(),
        listProjectJobs(
          {
            projectId: projectRef,
            kind: ['google_index', 'inspect'],
            since,
          },
          true,
        ).unwrap(),
      ]);
      setOverview(overviewData);
      setJobs(listedJobs);
    } catch (err) {
      setError(
        getIndexerErrorInfo(err).message || 'بارگذاری نمای کلی ناموفق بود',
      );
      setOverview(null);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [project, projectRef, rangeDays, getProjectOverview, listProjectJobs]);

  useEffect(() => {
    void load();
  }, [load]);

  const rangedJobs = useMemo(
    () => jobsInRange(jobs, rangeDays),
    [jobs, rangeDays],
  );
  const indexJobs = useMemo(
    () => rangedJobs.filter((job) => job.kind === 'google_index'),
    [rangedJobs],
  );
  const inspectJobs = useMemo(
    () => rangedJobs.filter((job) => job.kind === 'inspect'),
    [rangedJobs],
  );

  const inventory = overview?.inventory;
  const inventorySlices = useMemo(
    () =>
      inventory
        ? inventoryDonutSlices(inventory, {
            neverLabel: 'بدون بازرسی در بازه',
          })
        : [],
    [inventory],
  );
  const coverageRows = useMemo(
    () =>
      overview
        ? coverageTableRows(overview.coverage, overview.inventory.total, {
            neverLabel: 'بدون بازرسی در بازه',
          })
        : [],
    [overview],
  );
  const changesSlices = useMemo(
    () => (overview ? changesDonutSlices(overview.changes) : []),
    [overview],
  );

  const indexDaily = useMemo(
    () => aggregateIndexJobsByDay(indexJobs, rangeDays),
    [indexJobs, rangeDays],
  );
  const inspectDaily = useMemo(
    () => aggregateInspectJobsByDay(inspectJobs, rangeDays),
    [inspectJobs, rangeDays],
  );

  const googleUsedInRange = useMemo(
    () => googleIndexingUsedInRange(jobs, rangeDays),
    [jobs, rangeDays],
  );
  const inspectUsedInSelectedRange = useMemo(
    () => inspectUsedInRange(jobs, rangeDays),
    [jobs, rangeDays],
  );
  const rangeHint = statsRangeHint(rangeDays);

  const indexedPct = inventory
    ? pctOf(
        inventory.indexed,
        Math.max(1, inventory.total - inventory.neverInspected),
      )
    : 0;

  const kpiCards = useMemo((): KpiCard[] => {
    if (!inventory) return [];
    return [
      {
        id: 'indexed',
        label: 'ایندکس‌شده',
        value: formatFaNumber(inventory.indexed),
        hint: `${formatFaNumber(indexedPct)}٪ از بازرسی‌شده‌ها · ${rangeHint}`,
        tone: 'success',
        href: urlsPath({ status: 'indexed', days: rangeDays, base: paths.urls }),
      },
      {
        id: 'not-indexed',
        label: 'ایندکس‌نیست',
        value: formatFaNumber(inventory.notIndexed),
        hint: `${formatFaNumber(pctOf(inventory.notIndexed, Math.max(1, inventory.total - inventory.neverInspected)))}٪ · ${rangeHint}`,
        tone: 'warning',
        href: urlsPath({ status: 'not_indexed', days: rangeDays, base: paths.urls }),
      },
      {
        id: 'error',
        label: 'خطای بازرسی',
        value: formatFaNumber(inventory.error),
        hint: rangeHint,
        tone: 'danger',
        href: urlsPath({ status: 'error', days: rangeDays, base: paths.urls }),
      },
      {
        id: 'never',
        label: 'بدون بازرسی در بازه',
        value: formatFaNumber(inventory.neverInspected),
        hint: rangeHint,
        tone: 'neutral',
        href: urlsPath({ status: 'never_inspected', days: rangeDays, base: paths.urls }),
      },
      {
        id: 'indexing-usage',
        label: 'درخواست ایندکس',
        value: formatFaNumber(googleUsedInRange),
        hint: `سقف روزانه ${formatFaNumber(GOOGLE_DAILY_QUOTA)} · ${rangeHint}`,
        tone:
          rangeDays === 1 && googleUsedInRange >= GOOGLE_DAILY_QUOTA
            ? 'danger'
            : rangeDays === 1 && googleUsedInRange >= GOOGLE_DAILY_QUOTA * 0.9
              ? 'warning'
              : 'primary',
        help: <IndexLimitHelpTip />,
        href: paths.index,
      },
      {
        id: 'inspect-usage',
        label: 'وضعیت ایندکس',
        value: formatFaNumber(inspectUsedInSelectedRange),
        hint: `سقف روزانه ${formatFaNumber(INSPECT_DAILY_QUOTA)} · ${rangeHint}`,
        tone:
          rangeDays === 1 && inspectUsedInSelectedRange >= INSPECT_DAILY_QUOTA
            ? 'danger'
            : rangeDays === 1 &&
                inspectUsedInSelectedRange >= INSPECT_DAILY_QUOTA * 0.9
              ? 'warning'
              : 'primary',
        help: <InspectQuotaHelpTip />,
        href: paths.urlInspection,
      },
    ];
  }, [
    inventory,
    indexedPct,
    googleUsedInRange,
    inspectUsedInSelectedRange,
    rangeHint,
    rangeDays,
    paths,
  ]);

  if (!project || !projectRef) return <NeedProject />;

  return (
    <div className="overview-page d-flex flex-column gap-3">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <OverviewTabSwitch
        value={pageTab}
        onChange={setPageTab}
        aside={
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Form.Select
              size="sm"
              className="w-auto"
              value={rangeDays}
              onChange={(e) =>
                setRangeDays(Number(e.target.value) as StatsRange)
              }
              aria-label="بازه زمانی نمای کلی"
            >
              {STATS_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => void load()}
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ width: 12, height: 12 }}
                />
              ) : (
                <IndexerIcon name="arrow-clockwise" />
              )}
            </Button>
          </div>
        }
      />

      {loading && !overview ? (
        <OverviewPagePreload />
      ) : (
        <>
          <div>
            {pageTab === 'summary' ? (
              <div className="d-flex flex-column gap-3">
          <div className="overview-kpi-grid">
            {kpiCards.map((card, index) => (
              <IndexerKpiCard
                key={card.id}
                tone={card.tone}
                imageIndex={index}
                className={
                  card.href ? 'overview-kpi-card--clickable' : undefined
                }
                role={card.href ? 'link' : undefined}
                tabIndex={card.href ? 0 : undefined}
                onClick={
                  card.href
                    ? () => {
                        navigate(card.href!);
                      }
                    : undefined
                }
                onKeyDown={
                  card.href
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(card.href!);
                        }
                      }
                    : undefined
                }
              >
                <div className="overview-kpi-value">
                  <span>{card.value}</span>
                </div>
                {card.label && (
                  <div className="text-secondary small d-inline-flex align-items-center gap-1">
                    <span>{card.label}</span>
                    {card.help}
                  </div>
                )}
                {!card.label && card.help ? (
                  <div className="fs--1">{card.help}</div>
                ) : null}
                {card.hint && (
                  <div className="overview-kpi-hint">{card.hint}</div>
                )}
              </IndexerKpiCard>
            ))}
          </div>

          <div className="row g-3">
            <div className="col-lg-5">
              <IndexerChartCard
                title="توزیع وضعیت ایندکس"
                subtitle={`توزیع آخرین بازرسی در بازهٔ ${rangeHint}`}
                guideKey="overview-index-status"
                endEl={
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fs--2"
                    onClick={() => navigate(paths.urls)}
                  >
                    لیست آدرس‌ها
                  </Button>
                }
              >
                <DonutChart
                  slices={inventorySlices}
                  centerValue={
                    inventory
                      ? `${formatFaNumber(indexedPct)}٪`
                      : undefined
                  }
                  centerLabel="ایندکس‌شده"
                  onSliceClick={(slice) => {
                    const status = inventorySliceToStatus(slice.id);
                    if (!status) return;
                    navigate(urlsPath({ status, days: rangeDays, base: paths.urls }));
                  }}
                />
              </IndexerChartCard>
            </div>
            <div className="col-lg-7">
              <IndexerChartCard
                title="جزئیات Coverage"
                subtitle={`تفکیک وضعیت گوگل در بازهٔ ${rangeHint}`}
                guideKey="overview-coverage"
                bodyClassName="d-flex flex-column"
              >
                <CoverageBreakdownTable
                  rows={coverageRows}
                  total={inventory?.total ?? 0}
                  onRowClick={(row) => {
                    navigate(
                      urlsPath({ coverage: row.id, days: rangeDays , base: paths.urls }),
                    );
                  }}
                />
              </IndexerChartCard>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-6">
              <IndexerChartCard
                title="درخواست ایندکس"
                subtitle={`آدرس‌های موفق و ناموفق در بازهٔ ${rangeHint}`}
                guideKey="overview-index-requests"
                endEl={
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fs--2"
                    onClick={() => navigate(paths.index)}
                  >
                    ارسال درخواست
                  </Button>
                }
              >
                <StackedBarChart
                  data={indexDaily}
                  series={indexOutcomeBarSeries()}
                />
              </IndexerChartCard>
            </div>
            <div className="col-lg-6">
              <IndexerChartCard
                title="وضعیت ایندکس"
                subtitle={`نتیجهٔ URL Inspection در بازهٔ ${rangeHint}`}
                guideKey="overview-inspect-activity"
                endEl={
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fs--2"
                    onClick={() => navigate(paths.urlInspection)}
                  >
                    بازرسی جدید
                  </Button>
                }
              >
                <StackedBarChart
                  data={inspectDaily}
                  series={inspectDailyBarSeries()}
                />
              </IndexerChartCard>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-5">
              <IndexerChartCard
                title="تغییرات وضعیت"
                subtitle={`وقتی بازرسی وضعیت ایندکس را در بازهٔ ${rangeHint} عوض کند`}
                guideKey="overview-status-changes"
                bodyClassName="d-flex flex-column justify-content-center"
                endEl={
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fs--2"
                    onClick={() => navigate(paths.changes)}
                  >
                    مشاهدهٔ همه
                  </Button>
                }
              >
                <DonutChart
                  slices={changesSlices}
                  centerValue={
                    overview
                      ? formatFaNumber(overview.changes.total)
                      : undefined
                  }
                  centerLabel="تغییر"
                  onSliceClick={(slice) => {
                    const filter = changesSliceToFilter(slice.id);
                    if (!filter) return;
                    navigate(changesPath({ filter, days: rangeDays, base: paths.changes }));
                  }}
                />
              </IndexerChartCard>
            </div>
            <div className="col-lg-7">
              <IndexerChartCard
                title="خلاصهٔ تنظیمات"
                subtitle="وضعیت زمان‌بندی‌ها، اطلاع‌رسانی و گوگل"
                guideKey="overview-settings-checklist"
              >
                <OverviewSettingsChecklist projectId={projectRef} />
              </IndexerChartCard>
            </div>
          </div>
              </div>
            ) : (
              <OverviewAdvancedCharts
                projectId={projectRef}
                rangeDays={rangeDays}
                embedded
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
